import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { Lock as LockIcon, LockOpen as UnlockIcon } from '@mui/icons-material';
import * as vault from 'shared/utils/byokVault';

/**
 * Set up, unlock or remove the user's own AI API key (stored encrypted on this device
 * only). onChange() is called whenever the vault status changes.
 */
const OwnKeyPanel = ({ userId, onChange }) => {
  const info = vault.status(userId);
  const [form, setForm] = useState({ provider: 'anthropic', model: 'claude-opus-5-5', apiKey: '', passphrase: '', confirm: '' });
  const [passphrase, setPassphrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try { await fn(); onChange?.(); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const provider = vault.PROVIDERS[form.provider];

  if (info.configured && !editing) {
    return (
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Chip size="small" icon={info.unlocked ? <UnlockIcon /> : <LockIcon />}
            color={info.unlocked ? 'success' : 'default'}
            label={`${vault.PROVIDERS[info.provider]?.label || info.provider} · ${info.model} · ${info.unlocked ? 'unlocked' : 'locked'}`} />
          {info.unlocked && <Button size="small" onClick={() => run(async () => vault.lock())}>Lock now</Button>}
          <Button size="small" color="error" onClick={() => run(async () => vault.forgetKey(userId))}>Remove from this device</Button>
          <Button size="small" onClick={() => setEditing(true)}>Replace key</Button>
        </Stack>
        {!info.unlocked && (
          <Stack direction="row" spacing={1}>
            <TextField size="small" type="password" label="Passphrase" value={passphrase} autoComplete="current-password"
              onChange={(e) => setPassphrase(e.target.value)} sx={{ flex: 1 }}
              onKeyDown={(e) => { if (e.key === 'Enter' && passphrase) run(() => vault.unlock(userId, passphrase).then(() => setPassphrase(''))); }} />
            <Button variant="contained" disabled={!passphrase || busy}
              onClick={() => run(() => vault.unlock(userId, passphrase).then(() => setPassphrase('')))}
              startIcon={busy ? <CircularProgress size={16} /> : <UnlockIcon />}>
              Unlock
            </Button>
          </Stack>
        )}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </Box>
    );
  }

  const mismatch = form.confirm && form.passphrase !== form.confirm;
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Your key is encrypted in this browser with a passphrase only you know and is sent only to the AI provider —
        never to Otable. Nobody at Otable, including administrators, can read it. If you forget the passphrase,
        just remove and re-enter the key.
      </Typography>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField select size="small" label="Provider" value={form.provider} sx={{ minWidth: 200 }}
            onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value, model: vault.PROVIDERS[e.target.value].models[0]?.id || '' }))}>
            {Object.entries(vault.PROVIDERS).map(([id, p]) => <MenuItem key={id} value={id}>{p.label}</MenuItem>)}
          </TextField>
          {provider.models.length ? (
            <TextField select size="small" label="Model" value={form.model} onChange={set('model')} sx={{ flex: 1 }}>
              {provider.models.map((m) => <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>)}
            </TextField>
          ) : (
            <TextField size="small" label="Model" value={form.model} onChange={set('model')} sx={{ flex: 1 }}
              helperText="The model name enabled on your OpenAI account" />
          )}
        </Stack>
        <TextField size="small" type="password" label="API key" value={form.apiKey} onChange={set('apiKey')}
          autoComplete="off" placeholder={`${provider.keyPrefix}…`} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField size="small" type="password" label="Passphrase (10+ characters)" value={form.passphrase}
            onChange={set('passphrase')} autoComplete="new-password" sx={{ flex: 1 }} />
          <TextField size="small" type="password" label="Repeat passphrase" value={form.confirm}
            onChange={set('confirm')} autoComplete="new-password" sx={{ flex: 1 }}
            error={Boolean(mismatch)} helperText={mismatch ? 'Passphrases differ' : ' '} />
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {info.configured && <Button onClick={() => setEditing(false)}>Cancel</Button>}
          <Button variant="contained"
            disabled={busy || !form.apiKey || !form.model || form.passphrase.length < 10 || form.passphrase !== form.confirm}
            onClick={() => run(async () => {
              await vault.saveKey(userId, form);
              setForm((f) => ({ ...f, apiKey: '', passphrase: '', confirm: '' })); // drop the plaintext from state
              setEditing(false);
            })}
            startIcon={busy ? <CircularProgress size={16} /> : <LockIcon />}>
            Encrypt and save on this device
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default OwnKeyPanel;
